<template>
  <div class="table-responsive table-hover">
    <table class="table collapsible-list-config" style="width:100%">
      <thead>
        <tr class="collapsible-list-config-row">
          <th>Stock</th>
          <th>Alias</th>
          <template v-for="(header, index) in headerList">
            <th :key="index" class="collapsible-list-config-cell text-center" v-html="header.name"></th>
            <th :key="`${index}-override`" class="collapsible-list-config-cell list-priceoverride-cell text-center" width="30px"> </th>
          </template>
          <th width="0%"> </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item, index) in priceData">
          <tr :key="index" class="collapsible-list-config-row-header" v-show="isCategoryParentExpanded(item)" v-if="item.type == 'category'">
            <td class="text-capitalize font-bold" @click="categoryBarClicked($event, item, true)">
              <span v-html="categoryTitle(item)"></span>
            </td>
            <td @click="categoryBarClicked($event, item, false)">
              <span class="font-italic" v-if="!editing">
                {{item.alias}}
              </span>
              <span v-else>
                <FormInputText variant="collapsible-list-alias-input" :value="item.alias" @input="categoryAliasUpdated(item, $event)" placeholder="Category Alias" />
              </span>
            </td>
            <template v-for="(header, headerIndex) in headerList">
              <td
                :key="headerIndex"
                class="text-center"
                @click="categoryBarClicked($event, item, false)">

                <span v-if="!editing">
                  <i class="fas fa-check permission-allowed" v-if="getSwitchValue(item, header) == 'checked'"> </i>
                  <i class="fas fa-circle permission-mixed" v-if="getSwitchValue(item, header) == 'indeterminate'"> </i>
                  <i class="fas fa-ban permission-denied" v-if="getSwitchValue(item, header) == 'unchecked'"> </i>
                </span>
                <span v-else>
                  <FormSwitch 
                    :value="getSwitchValue(item, header)"
                    @update="categorySwitchUpdated(item, header, $event)"
                  />
                </span>
              </td>
              <td :key="`${headerIndex}-override`" class="text-center list-priceoverride-cell" @click="categoryBarClicked($event, item, false)" width="30px"> </td>
            </template>
            <td>
              <ActionDropMenu v-if="editing" :tableAction="true">
                <DropdownMenuItem @click="setCategoryRow(item, true)" icon="fa-check">Show All</DropdownMenuItem>
                <DropdownMenuItem @click="setCategoryRow(item, false)" icon="fa-times">Hide All</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>

          <tr :key="index" class="" v-show="isStockParentExpanded(item)" v-if="item.type == 'stock'">
            <td class="text-capitalize" colspan="2">  <!-- Only use aliases for categories currently -->
              <span v-html="stockTitle(item)"></span>
            </td>
            <template v-for="(header, headerIndex) in headerList">
              <td
                :key="headerIndex"
                class="text-center"
                @click="categoryBarClicked($event, item, false)">
                
                <span v-if="!editing">
                  <i class="fas fa-check permission-allowed" v-if="getSwitchValue(item, header) == 'checked'"> </i>
                  <i class="fas fa-minus permission-inherited" v-if="getSwitchValue(item, header) == 'indeterminate'"> </i>
                  <i class="fas fa-ban permission-denied" v-if="getSwitchValue(item, header) == 'unchecked'"> </i>
                </span>
                <span v-else>
                  <FormSwitch :value="getSwitchValue(item, header)" :inherited="getInheritedValue(item, header)" @update="stockSwitchUpdated(item, header, $event)" />
                </span>
              </td>
              <td :key="`${headerIndex}-override`" class="text-center list-priceoverride-cell" @click="categoryBarClicked($event, item, false)" width="30px">
                <span v-if="!editing">
                  <span class="text-muted" v-if="hasOverrideText(item, header)">Override: {{ getOverrideText(item, header) }}</span>
                </span>
                <span v-else>
                  <a href="#" @click.prevent="displayOverridePopover($event, item, header)">{{ getOverrideDisplayLink(item, header) }}</a>
                </span>
              </td>
            </template>
            <td>
              <ActionDropMenu v-if="editing" :tableAction="true">
                <DropdownMenuItem @click="setStockRow(item, true)" icon="fa-check">Show All</DropdownMenuItem>
                <DropdownMenuItem @click="setStockRow(item, false)" icon="fa-times">Hide All</DropdownMenuItem>
              </ActionDropMenu>
            </td>
          </tr>

          <tr v-show="item.expanded && isCategoryParentExpanded(item)" :key="`empty-cat-${index}`" v-if="item.type == 'category' && item.childStockCount + item.childCategoryCount == 0">
            <td :colspan="headerList.length+1" class="font-italic text-muted" v-html="emptyCategoryTitle(item)"></td>
          </tr>

        </template>
      </tbody>
    </table>

    <Popover ref="overridePopover" :showHeader="true" headerTitle="Price Override">
      <div class="p-2">
        <FormInputText
          ref="overrideEntry"
          v-model="currentOverrideText"
          @enter="applyOverride">
        </FormInputText>

        <div class="row mt-2">
          <div class="col-6 pr-1">
            <button class="btn btn-sm btn-default" :disabled="!isOverride()" @click.prevent="removeOverride">
              <i class="far fa-trash-alt mr-2"></i>Remove Override
            </button>
          </div>
          <div class="col-6 pl-1">
            <button class="btn btn-sm btn-default" @click.prevent="applyOverride"><i class="far fa-check status-approved mr-2"></i> Apply Override</button>
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>


<style lang="scss" scoped>
.list-priceoverride-cell {
  width: 80px !important;
  padding-left: 1px !important;
  padding-right: 1px !important;

  font-size: 0.85em;
}
</style>


<script>
import _ from 'lodash'
import Popover from '@/components/popover'
import FormSwitch from '@/components/form/formSwitch.vue'   // Used for setting enum

import { InventoryStockTypes, PriceListConfigEntryTypes, PriceListConfigDisplayTypes } from '@/store/types'

export default {
  name: 'PriceListConfigList',
  components: {
    Popover
  },
  props: {
    editing: Boolean,
    isCustomer: {
      type: Boolean,
      default: false
    },
    stockType: String,
    priceListConfig: Array,
    priceGroupList: Array
  },
  watch: {
    priceListConfig: function(newVal, oldVal) {
      this.updatePriceConfigData(_.cloneDeep(newVal))
    }
  },
  data() {
    return {
      priceConfigData: [],
      currentOverrideText: '',
      selectedOverrideStockId: null,
      selectedOverridePriceGroupId: null,
      selectedOverrideServiceRangeId: null
    }
  },
  computed: {
    priceData() {
      return this.priceConfigData.filter(item => item.stockType == this.$props.stockType)
    },
    headerList() {
      if(this.stockType == InventoryStockTypes.glass) {
        return this.priceGroupList.filter(priceGroup => priceGroup.isActive).map(priceGroup => {
          return {
           stockPriceGroupId: priceGroup.id,
            name: priceGroup.name
          }
        })
      }
      if(this.stockType == InventoryStockTypes.glassService) {
        var headers = []
        this.priceGroupList.forEach(priceGroup => {
          if(priceGroup.isActive && priceGroup.servicePriceRanges && priceGroup.servicePriceRanges.length > 0) {
            headers = _.concat(headers, priceGroup.servicePriceRanges.map(serviceRange => {
              return {
               stockPriceGroupId: priceGroup.id,
               servicePriceRangeId: serviceRange.id,
                name: `${priceGroup.name}<br />${serviceRange.name}`
              }
            }))
          }
        })
        return headers
      }
      if(this.stockType == InventoryStockTypes.general) {
        return [{
          name: 'Price'
        }]
      }
      if(this.stockType == InventoryStockTypes.assembly) {
        return [{
          name: 'Price'
        }]
      }
      return []
    }
  },
  mounted() {
  },
  methods: {
    categoryTitle(category) {
      var seperator = '<i class="fas fa-caret-right"></i>'

      var arrow = category.expanded ? 'fa-angle-down' : 'fa-angle-right'
      var arrowIcon = `<i class="far ${arrow}"> </i>`

      return '&nbsp;'.repeat(category.level * 4) + ` ${arrowIcon} ${category.name}`
    },
    emptyCategoryTitle(category) {
      return '&nbsp;'.repeat(category.level * 4+3) + ` No stock`
    },
    stockTitle(stock) {
      return '&nbsp;'.repeat(stock.level * 4+2) + ` ${stock.name}`
    },
    categoryBarClicked(evt, category, nameColumn) {
      // Ignore clicks on permission switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      category.expanded = !category.expanded
      return true
    },
    isCategoryParentExpanded(category) {
      // Find parent
      var parentCategory = this.priceConfigData.find(cat => cat.type == PriceListConfigEntryTypes.category && cat.stockCategoryId == category.parentCategoryId)
      if(!parentCategory)
        return true
      if(!parentCategory.expanded)
        return false

      // Check hierarchy
      return this.isCategoryParentExpanded(parentCategory)
    },
    isStockParentExpanded(stock) {
      // Find parent
      var parentCategory = this.priceConfigData.find(cat => cat.type == PriceListConfigEntryTypes.category && cat.stockCategoryId == stock.stockCategoryId)
      if(!parentCategory)
        return true
      if(!parentCategory.expanded)
        return false

      // Check hierarchy of categories
      return this.isCategoryParentExpanded(parentCategory)
    },
    updatePriceConfigData(itemList) {
      itemList
      .forEach(item => {
        var curItem = this.$data.priceConfigData.find(matchItem => {
          if(item.type != matchItem.type)
            return false
          if(matchItem.type == PriceListConfigEntryTypes.category && matchItem.stockCategoryId == item.stockCategoryId)
            return true
          if(matchItem.type == PriceListConfigEntryTypes.stock && matchItem.stockId == item.stockId)
            return true
          return false
        })
        if(curItem != null) {
          curItem.alias = item.alias
          curItem.settings = item.settings
        } else {
          item.expanded = false
          this.$data.priceConfigData.push(item)
        }
      })

      this.updateAllCategoryStates()
    },
    setCategoryRow(category, value) {
      category.settings.forEach(setting => {
        setting.display = value ? PriceListConfigDisplayTypes.show : PriceListConfigDisplayTypes.hide

        this.categorySwitchUpdated(category, setting, value ? FormSwitch.ValueSettings.checked : FormSwitch.ValueSettings.unchecked)
      })
    },
    setStockRow(stock, value) {
      stock.settings.forEach(setting => {
        setting.display = value ? PriceListConfigDisplayTypes.show : PriceListConfigDisplayTypes.hide

        this.stockSwitchUpdated(stock, setting, value ? FormSwitch.ValueSettings.checked : FormSwitch.ValueSettings.unchecked)
      })
    },
    getSwitchValue(lineItem, column) {
      if(!lineItem.settings)
        return FormSwitch.ValueSettings.indeterminate

      var setting = lineItem.settings.find(setting => {
        return setting.stockPriceGroupId == column.stockPriceGroupId && 
        setting.servicePriceRangeId == column.servicePriceRangeId
      })

      if(!setting)
        return FormSwitch.ValueSettings.indeterminate
      if(setting.display == PriceListConfigDisplayTypes.show)
        return FormSwitch.ValueSettings.checked
      else if(setting.display == PriceListConfigDisplayTypes.hide)
        return FormSwitch.ValueSettings.unchecked
      else if(setting.display == PriceListConfigDisplayTypes.mixed)
        return FormSwitch.ValueSettings.indeterminate

      return FormSwitch.ValueSettings.indeterminate
    },
    getInheritedValue(lineItem, column) {
      if(!lineItem.settings)
        return false
      var setting = lineItem.settings.find(setting => {
        return setting.stockPriceGroupId == column.stockPriceGroupId && 
        setting.servicePriceRangeId == column.servicePriceRangeId
      })

      if(!setting)
        return false
      return setting.customerDisplay == PriceListConfigDisplayTypes.inherit
    },
    hasOverrideText(lineItem, column) {
      var overrideText = this.getOverrideText(lineItem, column)

      return (overrideText && overrideText.length > 0)
    },
    getOverrideText(lineItem, column) {
      if(!lineItem.settings)
        return ''
      var setting = lineItem.settings.find(setting => {
        return setting.stockPriceGroupId == column.stockPriceGroupId && 
        setting.servicePriceRangeId == column.servicePriceRangeId
      })

      if(!setting || !setting.overrideText)
        return ''
      return (setting.overrideText + '').trim()
    },
    getOverrideDisplayLink(lineItem, column) {
      var overrideText = this.getOverrideText(lineItem, column)

      if(overrideText && overrideText.length > 0)
        return `Override: ${overrideText}`
      return 'Override: None'
    },
    categorySwitchUpdated(category, column, value, nested) {

      // Update all children category/stock items
      this.$data.priceConfigData.forEach(item => {
        if(item.stockType != this.$props.stockType)
          return

        var updateItem = false

        if(item.type == PriceListConfigEntryTypes.category && item.parentCategoryId == category.stockCategoryId) {
          // Recursively drill down to child categories & apply the value
          this.categorySwitchUpdated(item, column, value, true)

          updateItem = true
        }

        if(item.type == PriceListConfigEntryTypes.stock && item.stockCategoryId == category.stockCategoryId) {
          updateItem = true
        }

        if(updateItem) {
          var setting = item.settings.find(setting => {
            return setting.stockPriceGroupId == column.stockPriceGroupId && 
            setting.servicePriceRangeId == column.servicePriceRangeId
          })

          if(setting) {
            setting.display = (value == FormSwitch.ValueSettings.checked ? PriceListConfigDisplayTypes.show : PriceListConfigDisplayTypes.hide)
            if(this.$props.isCustomer)
              setting.customerDisplay = setting.display
          }
        }
      })
      if(!nested) {
        this.updateCategoryStates(column)
        this.$emit('update', this.$data.priceConfigData)
      }
    },
    stockSwitchUpdated(stock, column, value) {

      this.$data.priceConfigData.forEach(item => {
        if(item.stockType != this.$props.stockType)
          return

        if(item.type == PriceListConfigEntryTypes.stock && item.stockId == stock.stockId) {
          var setting = item.settings.find(setting => {
            return setting.stockPriceGroupId == column.stockPriceGroupId && 
            setting.servicePriceRangeId == column.servicePriceRangeId
          })

          if(setting) {
            setting.display = (value == FormSwitch.ValueSettings.checked ? PriceListConfigDisplayTypes.show : PriceListConfigDisplayTypes.hide)
            if(this.$props.isCustomer)
              setting.customerDisplay = setting.display
          }
        }
      })

      // Update categories now that a stock was updated
      this.updateCategoryStates(column)
      this.$emit('update', this.$data.priceConfigData)
    },
    categoryAliasUpdated(category, value) {
      category.alias = value

      this.$data.priceConfigData.forEach(item => {
        if(item.stockType != this.$props.stockType)
          return

        if(item.type == PriceListConfigEntryTypes.category && item.stockCategoryId == category.stockCategoryId) {
          item.alias = value
        }
      })

      this.$emit('update', this.$data.priceConfigData)
    },

    updateAllCategoryStates() {
      this.headerList.forEach(column => {
        this.updateCategoryStates(column)
      })
    },

    updateCategoryStates(column) {
      // Update the category states if a stock was modified
      // If a category contains child stock/categories in a mixed state, change the state to interminate
      this.$data.priceConfigData.forEach((item, index) => {
        if(item.stockType != this.$props.stockType)
          return
        if(item.type != PriceListConfigEntryTypes.category)
          return

        var showCount = this.getDisplayStateCount(item.stockCategoryId, column, PriceListConfigDisplayTypes.show)
        var hideCount = this.getDisplayStateCount(item.stockCategoryId, column, PriceListConfigDisplayTypes.hide)

        // Don't change anything if no children
        if(showCount == 0 && hideCount == 0)
          return

        var display = PriceListConfigDisplayTypes.hide
        if(showCount > 0)
          display = PriceListConfigDisplayTypes.show
        if(showCount > 0 && hideCount > 0)
          display = PriceListConfigDisplayTypes.mixed

        var setting = item.settings.find(setting => {
          return setting.stockPriceGroupId == column.stockPriceGroupId && 
          setting.servicePriceRangeId == column.servicePriceRangeId
        })
        if(setting) {
          setting.display = display       
        }
      })
    },

    getDisplayStateCount(parentCategoryId, column, displayState) {
      var displayCount = 0

      this.$data.priceConfigData.forEach(item => {
        if(item.stockType != this.$props.stockType)
          return

        if(item.type == PriceListConfigEntryTypes.category && item.parentCategoryId == parentCategoryId) {
          // Drill down
          displayCount += this.getDisplayStateCount(item.stockCategoryId, column, displayState)
          return
        }

        if(item.type == PriceListConfigEntryTypes.stock && item.stockCategoryId == parentCategoryId) {
          var setting = item.settings.find(setting => {
            return setting.stockPriceGroupId == column.stockPriceGroupId && 
            setting.servicePriceRangeId == column.servicePriceRangeId
          })
          if(setting) {
            if(setting.display == displayState)
              displayCount++
          }
        }
      })

      return displayCount
    },

    displayOverridePopover(ev, lineItem, column) {
      this.selectedOverrideStockId = lineItem.stockId
      this.selectedOverridePriceGroupId = column.stockPriceGroupId
      this.selectedOverrideServiceRangeId = column.servicePriceRangeId

      this.currentOverrideText = this.getOverrideText(lineItem, column)

      // Show the popover
      this.$refs.overridePopover.displayFromElement(ev.target)

      this.$nextTick(() => {
        this.$refs.overrideEntry.select()
      })

    },
    isOverride() {
      return this.currentOverrideText && this.currentOverrideText.length > 0
    },
    applyOverride() {
      var lineItem = this.$data.priceConfigData.find(item => {
        return (item.type == PriceListConfigEntryTypes.stock && item.stockId == this.selectedOverrideStockId)
      })
      if(lineItem) {
        var setting = lineItem.settings.find(setting => {
          return setting.stockPriceGroupId == this.selectedOverridePriceGroupId && 
          setting.servicePriceRangeId == this.selectedOverrideServiceRangeId
        })
        if(setting) {
          setting.overrideText = this.currentOverrideText
        }
      }

      this.$refs.overridePopover.hidePopover()

      this.$emit('update', this.$data.priceConfigData)
    },
    removeOverride() {
      this.currentOverrideText = ''
      this.applyOverride()
    }
  }
}
</script>